import React, { useState, useEffect } from "react";
import {
  TextField,
  Grid,
  Button,
  Autocomplete,
  Container,
  Box,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
  branchpartyList,
  getFreightsByPartyIdApi,
  issueCreate,
} from "../../../Services/Services";

export default function IssueForm({ setIssues, setOpenAddModal }) {
  const emptyRow = {
    freight_id: "",
    branch_id: "",
    party_id: "",
    truck_no: "",
    recovery_amount: 0,
    description: "",
    freightOptions: [],
  };

  const [issueRows, setIssueRows] = useState([emptyRow]);
  const [partyOptions, setParties] = useState([]);
  const [branchOptions, setBranch] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const fetchData = async () => {
      const data = await branchpartyList();
      if (data) {
        setParties(data.party);
        setBranch(data.branch);
      }
    };
    fetchData();
  }, []);

  const handleChange = (index, name, value) => {
    const updatedRows = [...issueRows];
    updatedRows[index][name] = value;
    setIssueRows(updatedRows);
  };

  const handlePartyChange = async (index, newValue) => {
    const updatedRows = [...issueRows];
    const party_id = newValue?.party_id || "";
    updatedRows[index].party_id = party_id;
    updatedRows[index].freight_id = "";
    updatedRows[index].truck_no = "";
    updatedRows[index].freightOptions = [];

    if (party_id) {
      const freights = await getFreightsByPartyIdApi(party_id);
      updatedRows[index].freightOptions = freights || [];
    }

    setIssueRows(updatedRows);
  };

  const handleFreightChange = (index, newValue) => {
    const updatedRows = [...issueRows];
    updatedRows[index].freight_id = newValue?.freight_id || "";
    updatedRows[index].truck_no = newValue?.truck_no || "";
    updatedRows[index].recovery_amount =
      updatedRows[index].recovery_amount ?? 0;
    setIssueRows(updatedRows);
  };

  const handleAddRow = () => {
    setIssueRows([...issueRows, { ...emptyRow }]);
  };

  const handleRemoveRow = (index) => {
    const updated = [...issueRows];
    updated.splice(index, 1);
    setIssueRows(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = issueRows.map(({ freightOptions, ...rest }) => rest);
      const response = await issueCreate(payload);
      setIssues(response);
      setOpenAddModal(false);
      setSnackbar({
        open: true,
        message: "Issues created successfully!",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to create issues.",
        severity: "error",
      });
    }
  };

  return (
    <Container maxWidth="xl">
      <form onSubmit={handleSubmit}>
        {issueRows.map((row, index) => (
          <Grid
            container
            spacing={1}
            key={index}
            alignItems="center"
            sx={{ mb: 1 }}
          >
            {/* Branch */}
            <Grid item xs={2}>
              <Autocomplete
                options={branchOptions}
                getOptionLabel={(option) => option?.branch_name || ""}
                value={
                  branchOptions.find((b) => b.branch_id === row.branch_id) ||
                  null
                }
                onChange={(e, newValue) =>
                  handleChange(index, "branch_id", newValue?.branch_id || "")
                }
                renderInput={(params) => (
                  <TextField {...params} label="Branch" required />
                )}
              />
            </Grid>

            {/* Party */}
            <Grid item xs={2}>
              <Autocomplete
                options={partyOptions}
                getOptionLabel={(option) => option?.name || ""}
                value={
                  partyOptions.find((p) => p.party_id === row.party_id) || null
                }
                onChange={(e, newValue) => handlePartyChange(index, newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Party" required />
                )}
              />
            </Grid>

            {/* Freight */}
            <Grid item xs={1.5}>
              <Autocomplete
                options={row.freightOptions || []}
                getOptionLabel={(option) =>
                  option?.freight_id ? `${option.freight_id}` : ""
                }
                value={
                  row.freightOptions?.find(
                    (f) => f.freight_id === row.freight_id
                  ) || null
                }
                onChange={(e, newValue) => handleFreightChange(index, newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Freight ID" required />
                )}
              />
            </Grid>

            {/* Truck No */}
            <Grid item xs={1.5}>
              <TextField
                label="Truck No."
                value={row.truck_no}
                InputProps={{ readOnly: true }}
                fullWidth
              />
            </Grid>

            {/* Recovery Amount */}
            <Grid item xs={1.5}>
              <TextField
                label="Recovery Amount"
                type="number"
                value={row.recovery_amount}
                onChange={(e) =>
                  handleChange(
                    index,
                    "recovery_amount",
                    e.target.value === "" ? 0 : parseFloat(e.target.value)
                  )
                }
                fullWidth
                inputProps={{
                  min: 0,
                  step: "0.01",
                }}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={2.5}>
              <TextField
                label="Description"
                value={row.description}
                onChange={(e) =>
                  handleChange(index, "description", e.target.value)
                }
                fullWidth
                required
              />
            </Grid>

            {/* Delete */}
            <Grid item xs={0.5}>
              {issueRows.length > 1 && (
                <IconButton
                  color="error"
                  onClick={() => handleRemoveRow(index)}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Grid>
          </Grid>
        ))}

        {/* Add + Submit Buttons */}
        <Box display="flex" justifyContent="space-between" mt={3}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddRow}
          >
            Add Row
          </Button>
          <Button variant="contained" type="submit">
            Submit Issues
          </Button>
        </Box>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
